// FOTOS — separado em "Fotos do produto" (Base do produto: TODAS as fotos do
// canal) e "Fotos da cor" (Cor selecionada: so as marcadas com aquela cor),
// ADR-012 F2 + redesign W5 (plano secao 5 W5).
// Desenho: docs/specs/2026-09-21-produto-variavel-fotos-por-cor-grade-por-canal-design.md §7
//
// 2026-10-13: marcar cor na foto NAO a tira mais da Base. Antes a Base so
// listava colors=[] ("comuns"), e a operadora da Hiit (Erica) via a foto
// sumir do produto ao associar a uma cor. Agora a cor SOMA: a foto fica na
// galeria do produto e vira tambem a imagem da variacao daquela cor.
//
// Antes (pre-redesign) a aba unica "Fotos" repetia a lista de cores debaixo
// de CADA foto e mostrava todos os grupos empilhados — um dos 2 problemas
// estruturais que o redesign corrige (plano 0.0). Agora a cor e o eixo: o
// hook `usePhotoManager` centraliza estado/mutations (a MESMA logica de
// antes, byte a byte), e dois componentes burros renderizam so o grupo que
// a tela ativa precisa (Base -> principais; Cor -> so aquela cor).
//
// A ordem enviada ao backend continua sendo UM array achatado (endpoint
// photos/order); o dnd-kit ja resolve isso sozinho: cada SortableContext e
// so uma VIEW filtrada do mesmo estado `order` global.
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  GripVertical,
  ImageOff,
  Info,
  Loader2,
  MoreHorizontal,
  Search,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import type { ApiErrorPayload, MediaItem, ProductDetail } from "../lib/types";
import { EmptyState } from "./ui/empty-state";
import { Select } from "./ui/select";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import {
  mediaColors,
  isPendingUnmatched,
  manualDivergesFromFolder,
} from "../lib/photos";
import { ColorSwatch } from "./product/ColorSwatch";

/* ============================================================
 * ENVIO DE FOTOS PELA TELA (plano 2026-10-27-envio-de-fotos-pela-tela)
 * ------------------------------------------------------------
 * A API grava o arquivo na pasta do SKU no Drive (subpasta com o nome da cor,
 * quando ha cor) e o poller importa como sempre. A tela envia UM arquivo por
 * requisicao, em fila (D1), e depois reconsulta a ficha ate a foto chegar.
 * ============================================================ */
const UPLOAD_ACCEPT = "image/jpeg,image/png,image/webp";
const UPLOAD_MAX_FILES = 50;
const UPLOAD_MAX_MB = 10;
const UPLOAD_TIMEOUT_MS = 120_000;
const WATCH_INTERVAL_MS = 20_000;
const WATCH_MAX_MS = 8 * 60_000;
const IMPORT_ETA_TEXT = "7 minutos";
const HEIC_MESSAGE = "O iPhone mandou em HEIC. Exporte como JPG ou PNG e envie de novo.";
/** Erros que valem para o lote inteiro: nao adianta tentar os demais arquivos. */
const BATCH_STOP_CODES = new Set(["duplicate_sku_folder", "drive_unavailable", "drive_error"]);

type UploadStatus = "fila" | "enviando" | "enviada" | "importada" | "duplicada" | "erro";

interface UploadItem {
  id: string;
  name: string;
  /** Miniatura local (URL.createObjectURL), revogada ao sair da ficha. */
  previewUrl: string;
  /** null = Base (foto geral do produto). */
  color: string | null;
  status: UploadStatus;
  /** 0-100, so enquanto `enviando`. */
  progress: number;
  /** Motivo do erro. */
  message?: string;
}

interface UploadResponse {
  status: "uploaded" | "duplicate";
  drive_file_id: string;
  name: string;
  color: string | null;
  created_folders: { id: string; name: string }[];
  import_eta_seconds: number;
}

/** Arquivo solto do sistema (dataTransfer.types inclui "Files"), nao o arrastar de reordenar. */
function hasFiles(e: DragEvent | globalThis.DragEvent): boolean {
  const types = e.dataTransfer?.types;
  return types ? Array.from(types).includes("Files") : false;
}

/** Valida no navegador o que a API recusaria de qualquer jeito (poupa a ida e volta). */
function prevalidateUpload(file: File): string | null {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const type = file.type.toLowerCase();
  if (type === "image/heic" || type === "image/heif" || ext === "heic" || ext === "heif") return HEIC_MESSAGE;
  const okType = type === "image/jpeg" || type === "image/png" || type === "image/webp";
  const okExt = ["jpg", "jpeg", "png", "webp"].includes(ext);
  if (!okType && !(type === "" && okExt)) return "Formato não aceito. Use JPG, PNG ou WebP.";
  if (file.size > UPLOAD_MAX_MB * 1024 * 1024) return `Passa de ${UPLOAD_MAX_MB} MB. Reduza a foto e envie de novo.`;
  if (file.size === 0) return "Arquivo vazio.";
  return null;
}

function uploadErrorInfo(err: unknown): { code: string | null; message: string } {
  if (axios.isAxiosError<ApiErrorPayload>(err)) {
    const code = err.response?.data?.error ?? null;
    if (code === "heic_not_supported") return { code, message: HEIC_MESSAGE };
    if (code === "file_too_large") return { code, message: `Passa de ${UPLOAD_MAX_MB} MB. Reduza a foto e envie de novo.` };
    if (!err.response) {
      return {
        code: null,
        message:
          err.code === "ECONNABORTED"
            ? "Demorou demais para enviar. Tente de novo."
            : "Sem conexão com o servidor. Tente de novo.",
      };
    }
    return { code, message: apiErrorMessage(err) };
  }
  return { code: null, message: apiErrorMessage(err) };
}

/** Area que aceita arquivos soltos: realce na borda e `onFiles` no drop. So reage a
 *  arquivo do sistema, nunca ao arrastar de reordenar do dnd-kit (que e por ponteiro). */
function useFileDrop(onFiles: (files: File[]) => void, enabled: boolean) {
  const [isOver, setIsOver] = useState(false);
  const depth = useRef(0);
  const dropProps = {
    onDragEnter: (e: DragEvent) => {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setIsOver(true);
    },
    onDragOver: (e: DragEvent) => {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e: DragEvent) => {
      if (!enabled || !hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setIsOver(false);
    },
    onDrop: (e: DragEvent) => {
      if (!enabled || !hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setIsOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) onFiles(files);
    },
  };
  return { isOver, dropProps };
}

/** Realce do cartao enquanto se arrasta arquivo por cima (outline nao muda o layout). */
function dropHighlight(isOver: boolean): React.CSSProperties | undefined {
  return isOver
    ? {
        outline: ".125rem dashed var(--color-coral)",
        outlineOffset: "-.125rem",
        background: "var(--color-coral-soft)",
      }
    : undefined;
}

/* ============================================================
 * HOOK COMPARTILHADO — estado + mutations (identico ao PhotosTab antigo)
 * ============================================================ */
// Hook compartilhado por CommonPhotosCard/ColorPhotosCard (mesmo arquivo).
// eslint-disable-next-line react-refresh/only-export-components
export function usePhotoManager(product: ProductDetail, slug: string) {
  const qc = useQueryClient();
  const { user, can } = useAuth();
  const queryKey = ["product", slug, product.sku];

  const allowedChannels: string[] = useMemo(() => {
    if (!user) return [];
    if (can("channels.all")) {
      const set = new Set<string>([slug]);
      for (const m of product.media) for (const c of m.channels) set.add(c);
      return Array.from(set);
    }
    return user.allowed_channels;
  }, [user, can, slug, product.media]);

  const activeColors = product.sisplan.colors_active ?? [];

  const channelMedia = useMemo(
    () =>
      product.media
        .filter((m) => m.channels.includes(slug))
        .sort(
          (a, b) =>
            (a.display_order_in_channel ?? 0) - (b.display_order_in_channel ?? 0) || a.id - b.id,
        ),
    [product.media, slug],
  );

  const [order, setOrderState] = useState<number[]>(() => channelMedia.map((m) => Number(m.id)));
  const channelMediaIds = useMemo(() => channelMedia.map((m) => Number(m.id)), [channelMedia]);
  const channelMediaIdsKey = useMemo(
    () => [...channelMediaIds].sort((a, b) => a - b).join(","),
    [channelMediaIds],
  );
  const [syncedKey, setSyncedKey] = useState(channelMediaIdsKey);
  if (syncedKey !== channelMediaIdsKey) {
    const incomingSet = new Set(channelMediaIds);
    const currSet = new Set(order);
    const preserved = order.filter((id) => incomingSet.has(id));
    const fresh = channelMediaIds.filter((id) => !currSet.has(id));
    setOrderState([...preserved, ...fresh]);
    setSyncedKey(channelMediaIdsKey);
  }
  const setOrder = setOrderState;

  const orderMap = useMemo(() => new Map(product.media.map((m) => [Number(m.id), m])), [product.media]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const orderMutation = useMutation({
    mutationFn: async (ordered_media_ids: number[]) => {
      await api.put(`/api/channels/${slug}/products/${product.sku}/photos/order`, {
        ordered_media_ids,
      });
    },
    onSuccess: () => {
      toast.success("Ordem das fotos salva");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const coverMutation = useMutation({
    mutationFn: async (media_id: number) => {
      await api.post(`/api/channels/${slug}/products/${product.sku}/cover`, { media_id });
    },
    onSuccess: () => {
      toast.success("Capa do canal definida");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const channelsMutation = useMutation({
    mutationFn: async ({ media_id, channels }: { media_id: number; channels: string[] }) => {
      await api.put(`/api/channels/${slug}/products/${product.sku}/photos/${media_id}/channels`, {
        channels,
      });
    },
    onSuccess: () => {
      toast.success("Seleção de canais atualizada");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const colorsMutation = useMutation({
    mutationFn: async ({ media_id, colors }: { media_id: number; colors: string[] }) => {
      await api.put(`/api/channels/${slug}/products/${product.sku}/photos/${media_id}/colors`, {
        colors,
      });
    },
    onSuccess: () => {
      toast.success("Cor da foto atualizada. Vale na próxima republicação.");
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const [applyingFolder, setApplyingFolder] = useState<string | null>(null);
  const applyFolderColor = async (folder: string, ids: number[], color: string): Promise<void> => {
    setApplyingFolder(folder);
    try {
      await Promise.all(
        ids.map((id) =>
          api.put(`/api/channels/${slug}/products/${product.sku}/photos/${id}/colors`, {
            colors: [color],
          }),
        ),
      );
      toast.success(`${ids.length} foto${ids.length > 1 ? "s" : ""} marcada${ids.length > 1 ? "s" : ""} como ${color}`);
      void qc.invalidateQueries({ queryKey });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setApplyingFolder(null);
    }
  };

  /** Copia (adiciona) uma cor a N fotos existentes — usado pela acao em lote
   *  "Copiar fotos de outra cor" (espinha de cores, W5). Nao substitui as
   *  cores ja marcadas na foto, apenas soma `targetColor`. */
  const copyPhotosFromColor = async (fromColor: string, targetColor: string): Promise<void> => {
    const ids = order.filter((id) => {
      const m = orderMap.get(id);
      return m ? mediaColors(m).includes(fromColor) : false;
    });
    if (ids.length === 0) {
      toast.info(`"${fromColor}" não tem foto própria pra copiar.`);
      return;
    }
    try {
      await Promise.all(
        ids.map((id) => {
          const m = orderMap.get(id);
          const next = Array.from(new Set([...(m ? mediaColors(m) : []), targetColor]));
          return api.put(`/api/channels/${slug}/products/${product.sku}/photos/${id}/colors`, {
            colors: next,
          });
        }),
      );
      toast.success(`${ids.length} foto${ids.length > 1 ? "s" : ""} copiada${ids.length > 1 ? "s" : ""} de "${fromColor}" para "${targetColor}"`);
      void qc.invalidateQueries({ queryKey });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  /* ---------- Envio de fotos pela tela ---------- */
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const uploadsRef = useRef<UploadItem[]>([]);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const seqRef = useRef(0);
  const watchRef = useRef<{ timer: number; deadline: number; target: number } | null>(null);
  const channelCountRef = useRef(channelMedia.length);
  const hadNoPhotosRef = useRef(product.media.length === 0);
  const queryKeyRef = useRef(queryKey);
  // Lote que termina depois de sair da ficha nao inicia vigia nem toast (review).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  useEffect(() => {
    hadNoPhotosRef.current = product.media.length === 0;
    queryKeyRef.current = queryKey;
  });

  const stopWatch = (): void => {
    if (watchRef.current) {
      window.clearInterval(watchRef.current.timer);
      watchRef.current = null;
    }
  };

  // A foto chegou (o numero de fotos do canal cresceu o esperado): para de vigiar.
  useEffect(() => {
    channelCountRef.current = channelMedia.length;
    const w = watchRef.current;
    if (w && channelMedia.length >= w.target) {
      stopWatch();
      setUploads((prev) => prev.map((u) => (u.status === "enviada" ? { ...u, status: "importada" } : u)));
      void qc.invalidateQueries({ queryKey: ["channel-products"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMedia.length]);

  // Trocou de produto/canal: a lista de envios e a vigia eram do produto anterior.
  useEffect(() => {
    return () => {
      stopWatch();
      for (const u of uploadsRef.current) URL.revokeObjectURL(u.previewUrl);
      uploadsRef.current = [];
      setUploads([]);
    };
  }, [slug, product.sku]);

  /** `baseline` = fotos do canal no INICIO do lote (review: foto importada durante
   * o lote nao pode inflar o alvo). */
  const startWatch = (uploaded: number, baseline: number): void => {
    if (!mountedRef.current) return;
    const deadline = Date.now() + WATCH_MAX_MS;
    const current = watchRef.current;
    if (current) {
      current.target += uploaded;
      current.deadline = deadline;
      return;
    }
    const timer = window.setInterval(() => {
      const w = watchRef.current;
      if (!w) return;
      if (Date.now() >= w.deadline) {
        stopWatch();
        toast.info(
          "As fotos ainda não apareceram. Confira a pasta no Drive ou recarregue a página em alguns minutos.",
        );
        return;
      }
      void qc.invalidateQueries({ queryKey: queryKeyRef.current });
    }, WATCH_INTERVAL_MS);
    watchRef.current = { timer, deadline, target: baseline + uploaded };
  };

  const patchUpload = (id: string, patch: Partial<UploadItem>): void => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const clearFinishedUploads = (): void => {
    const keep = uploadsRef.current.filter((u) => u.status === "fila" || u.status === "enviando" || u.status === "enviada");
    for (const u of uploadsRef.current) {
      if (!keep.includes(u)) URL.revokeObjectURL(u.previewUrl);
    }
    setUploads(keep);
  };

  /** Envia `files` para a Base (`color` null) ou para a subpasta da cor, UM por vez. */
  const uploadFiles = (files: File[], color: string | null): Promise<void> => {
    if (files.length === 0) return Promise.resolve();
    if (files.length > UPLOAD_MAX_FILES) {
      toast.warning(`Dá pra enviar até ${UPLOAD_MAX_FILES} fotos por vez. Enviei as ${UPLOAD_MAX_FILES} primeiras; selecione o resto de novo depois.`);
    }
    const batch = files.slice(0, UPLOAD_MAX_FILES).map((file) => {
      seqRef.current += 1;
      const item: UploadItem = {
        id: `up-${seqRef.current}`,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
        color,
        status: "fila",
        progress: 0,
      };
      return { item, file };
    });
    setUploads((prev) => [...prev, ...batch.map((b) => b.item)]);

    const url = `/api/channels/${slug}/products/${product.sku}/photos/upload`;
    const hadNoPhotos = hadNoPhotosRef.current;
    const baseline = channelCountRef.current;

    const runBatch = async (): Promise<void> => {
      let uploaded = 0;
      let duplicates = 0;
      let failed = 0;
      let stopMessage: string | null = null;
      for (const { item, file } of batch) {
        if (stopMessage) {
          patchUpload(item.id, { status: "erro", message: stopMessage });
          failed += 1;
          continue;
        }
        const invalid = prevalidateUpload(file);
        if (invalid) {
          patchUpload(item.id, { status: "erro", message: invalid });
          failed += 1;
          continue;
        }
        patchUpload(item.id, { status: "enviando", progress: 0 });
        try {
          const form = new FormData();
          form.append("file", file);
          if (color) form.append("color", color);
          // O axios manda application/json por padrao: apagar para o navegador
          // montar o multipart com o boundary.
          const r = await api.post<UploadResponse>(url, form, {
            headers: { "Content-Type": undefined },
            timeout: UPLOAD_TIMEOUT_MS,
            onUploadProgress: (ev) => {
              const total = ev.total ?? file.size;
              patchUpload(item.id, { progress: total > 0 ? Math.min(100, Math.round((ev.loaded / total) * 100)) : 0 });
            },
          });
          if (r.data.status === "duplicate") {
            duplicates += 1;
            patchUpload(item.id, { status: "duplicada", progress: 100 });
          } else {
            uploaded += 1;
            patchUpload(item.id, { status: "enviada", progress: 100 });
          }
        } catch (e) {
          const info = uploadErrorInfo(e);
          failed += 1;
          patchUpload(item.id, { status: "erro", message: info.message });
          if (info.code && BATCH_STOP_CODES.has(info.code)) stopMessage = info.message;
        }
      }

      if (uploaded > 0 && mountedRef.current) {
        toast.success(
          `${uploaded} foto${uploaded > 1 ? "s enviadas" : " enviada"} · ${uploaded > 1 ? "aparecem" : "aparece"} aqui em até ${IMPORT_ETA_TEXT}`,
          hadNoPhotos
            ? { description: "Este produto ainda não tinha foto: a primeira foto coloca o produto na loja." }
            : undefined,
        );
        startWatch(uploaded, baseline);
      }
      if (duplicates > 0) {
        toast.info(
          duplicates > 1
            ? `${duplicates} fotos já estavam no Drive. Não enviei de novo.`
            : "Essa foto já estava no Drive. Não enviei de novo.",
        );
      }
      if (failed > 0) {
        toast.error(
          failed > 1
            ? `${failed} fotos não foram enviadas. O motivo de cada uma está na lista de envios.`
            : "Uma foto não foi enviada. O motivo está na lista de envios.",
        );
      }
    };

    queueRef.current = queueRef.current.then(runBatch, runBatch);
    return queueRef.current;
  };

  const [activeDragId, setActiveDragId] = useState<number | null>(null);

  const onDragStart = (e: DragStartEvent): void => {
    setActiveDragId(Number(e.active.id));
  };

  const onDragCancel = (): void => {
    setActiveDragId(null);
  };

  const onDragEnd = (e: DragEndEvent): void => {
    setActiveDragId(null);
    if (!e.over || e.over.id === e.active.id) return;
    const activeId = Number(e.active.id);
    const overId = Number(e.over.id);
    setOrder((curr) => {
      const oldIdx = curr.indexOf(activeId);
      const newIdx = curr.indexOf(overId);
      if (oldIdx < 0 || newIdx < 0) return curr;
      const next = arrayMove(curr, oldIdx, newIdx);
      orderMutation.mutate(next);
      return next;
    });
  };

  const driveFolderId = product.entry.drive_folder_id;
  const folderName = `${product.sku} - ${product.entry.name_base ?? ""}`.trim().replace(/\s*-\s*$/, "");
  const driveUrl = driveFolderId
    ? `https://drive.google.com/drive/folders/${driveFolderId}`
    : "https://drive.google.com/drive/folders/0AKTsdMPiAm2WUk9PVA";

  // Base do produto = TODAS as fotos do canal na ordem curada (`order` ja e
  // so o que esta neste canal). Marcar cor nao tira a foto daqui.
  const productIds = order;
  const colorIds = (color: string): number[] =>
    order.filter((id) => {
      const m = orderMap.get(id);
      return m ? mediaColors(m).includes(color) : false;
    });

  const pendingByFolder = new Map<string, number[]>();
  for (const id of channelMediaIds) {
    const m = orderMap.get(id);
    if (m && isPendingUnmatched(m) && m.drive_subfolder) {
      const arr = pendingByFolder.get(m.drive_subfolder) ?? [];
      arr.push(id);
      pendingByFolder.set(m.drive_subfolder, arr);
    }
  }

  const notInChannel = product.media.filter((m) => !m.channels.includes(slug));

  return {
    allowedChannels,
    activeColors,
    order,
    orderMap,
    sensors,
    onDragStart,
    onDragEnd,
    onDragCancel,
    activeDragId,
    orderMutation,
    coverMutation,
    channelsMutation,
    colorsMutation,
    applyingFolder,
    applyFolderColor,
    copyPhotosFromColor,
    driveUrl,
    folderName,
    productIds,
    colorIds,
    pendingByFolder,
    notInChannel,
    uploads,
    uploadFiles,
    clearFinishedUploads,
  };
}

export type PhotoManager = ReturnType<typeof usePhotoManager>;

/* ============================================================
 * FOTOS DO PRODUTO (Base do produto) — todas as fotos do canal
 * ============================================================ */
export function CommonPhotosCard({
  product,
  slug,
  disabled,
  mgr,
}: {
  product: ProductDetail;
  slug: string;
  disabled: boolean;
  mgr: PhotoManager;
}): JSX.Element {
  const drop = useFileDrop((files) => void mgr.uploadFiles(files, null), !disabled);
  const uploads = mgr.uploads.filter((u) => u.color === null);
  const driveLink = (
    <a
      href={mgr.driveUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={disabled ? "btn btn--primary" : "btn btn--secondary"}
    >
      <ExternalLink className="icon" aria-hidden />
      {product.entry.drive_folder_id ? "Abrir pasta no Drive" : "Abrir 01 - Atacado no Drive"}
    </a>
  );

  if (product.media.length === 0) {
    const driveText = product.entry.drive_folder_id
      ? `soltar as fotos na pasta "${mgr.folderName}" no Drive compartilhado (01 - Atacado)`
      : `criar a pasta "${mgr.folderName}" dentro de "01 - Atacado" no Drive e soltar as fotos nela`;
    return (
      <div {...drop.dropProps} style={{ borderRadius: "var(--radius-lg)", ...dropHighlight(drop.isOver) }}>
        <EmptyState
          icon={<ImageOff className="h-12 w-12" />}
          title="Nenhuma foto detectada"
          description={
            disabled
              ? `Peça a quem tem acesso para ${driveText}. Importação automática em até ${IMPORT_ETA_TEXT}.`
              : `Envie as fotos por aqui (botão abaixo ou arraste os arquivos para esta área) ou, se preferir, ${driveText}. Os dois valem. Aparecem aqui em até ${IMPORT_ETA_TEXT}.`
          }
          action={
            <>
              <UploadButton color={null} disabled={disabled} onFiles={(files) => void mgr.uploadFiles(files, null)} />
              {driveLink}
            </>
          }
        />
        <UploadList items={uploads} onClearFinished={mgr.clearFinishedUploads} />
      </div>
    );
  }

  return (
    <div className="card" {...drop.dropProps} style={dropHighlight(drop.isOver)}>
      <div className="spread">
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>Fotos do produto</div>
        <span className="card__meta">todas aparecem na galeria do produto, com ou sem cor · ★ é a capa do canal</span>
        <a href={mgr.driveUrl} target="_blank" rel="noopener noreferrer" className="btn btn--secondary">
          <ExternalLink className="icon" aria-hidden />
          Abrir pasta no Drive
        </a>
      </div>

      <div className="stack" style={{ marginTop: ".75rem", gap: ".75rem" }}>
        <div className="cluster" style={{ alignItems: "flex-start", background: "var(--color-soft)", borderRadius: "var(--radius-md)", padding: ".625rem .75rem" }}>
          <Info className="icon" aria-hidden style={{ flexShrink: 0, marginTop: ".125rem" }} />
          <span className="field__hint grow">
            Toda foto fica na galeria do produto. Marcar uma cor só acrescenta: a foto vira também a
            imagem daquela cor na loja, sem sair daqui.{" "}
            {disabled
              ? `Foto nova entra pelo Drive, na pasta "${mgr.folderName}".`
              : <>Para colocar foto nova, envie por aqui (clique em <b>+ Enviar fotos</b> ou arraste os arquivos para este cartão) ou solte no Drive, na pasta "{mgr.folderName}": os dois valem.</>}{" "}
            No Drive, foto de uma cor vai na subpasta com o nome da cor; foto geral fica solta na pasta.
            Aparece aqui em até {IMPORT_ETA_TEXT}. O que você marcar aqui vale mais que a pasta.
          </span>
        </div>

        {[...mgr.pendingByFolder.entries()].map(([folder, ids]) => (
          <PendingFolderBlock
            key={folder}
            folder={folder}
            count={ids.length}
            activeColors={mgr.activeColors}
            disabled={disabled}
            applying={mgr.applyingFolder === folder}
            onApply={(color) => void mgr.applyFolderColor(folder, ids, color)}
          />
        ))}

        <DndContext
          sensors={mgr.sensors}
          collisionDetection={closestCorners}
          onDragStart={mgr.onDragStart}
          onDragEnd={mgr.onDragEnd}
          onDragCancel={mgr.onDragCancel}
        >
          <PhotoGroup
            description={`${mgr.productIds.length} foto${mgr.productIds.length !== 1 ? "s" : ""}`}
            hint="Arraste para reordenar. A ordem aqui é a ordem da galeria do produto na loja; a etiqueta mostra a cor que a foto também representa."
            ids={mgr.productIds}
            orderMap={mgr.orderMap}
            allowedChannels={mgr.allowedChannels}
            activeColors={mgr.activeColors}
            slug={slug}
            disabled={disabled || mgr.orderMutation.isPending}
            onSetCover={(id) => mgr.coverMutation.mutate(id)}
            onToggleChannel={(id, channels) => mgr.channelsMutation.mutate({ media_id: id, channels })}
            onSetColors={(id, colors) => mgr.colorsMutation.mutate({ media_id: id, colors })}
            uploadColor={null}
            uploadDisabled={disabled}
            onUploadFiles={(files) => void mgr.uploadFiles(files, null)}
          />
          <DragOverlay>
            {mgr.activeDragId != null && mgr.orderMap.get(mgr.activeDragId) ? (
              <div className="card" style={{ padding: ".5rem" }}>
                <div className="thumb" style={{ width: "6rem", height: "6rem" }}>
                  <img src={mgr.orderMap.get(mgr.activeDragId)!.url} alt="" draggable={false} className="h-full w-full object-cover select-none" />
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        <UploadList items={uploads} onClearFinished={mgr.clearFinishedUploads} />

        {mgr.notInChannel.length > 0 ? (
          <div>
            <div className="mb-1.5">
              <h3 style={{ fontSize: "var(--text-lg)", fontWeight: 900 }}>
                Disponíveis no Drive mas não neste canal ({mgr.notInChannel.length})
              </h3>
              <p className="field__hint">Marque o canal pra adicionar.</p>
            </div>
            <div className="cards cards--photos">
              {mgr.notInChannel.map((m) => (
                <PhotoCard
                  key={m.id}
                  media={m}
                  allowedChannels={mgr.allowedChannels}
                  activeColors={mgr.activeColors}
                  currentSlug={slug}
                  onSetCover={() => mgr.coverMutation.mutate(m.id)}
                  onToggleChannel={(channels) => mgr.channelsMutation.mutate({ media_id: m.id, channels })}
                  onSetColors={(colors) => mgr.colorsMutation.mutate({ media_id: m.id, colors })}
                  disabled={disabled}
                  notInChannel
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ============================================================
 * FOTOS DA COR (Cor selecionada)
 * ============================================================ */
export function ColorPhotosCard({
  color,
  slug,
  disabled,
  mgr,
  onCopyFromColor,
}: {
  color: string;
  slug: string;
  disabled: boolean;
  mgr: PhotoManager;
  onCopyFromColor: (fromColor: string) => void;
}): JSX.Element {
  const ids = mgr.colorIds(color);
  const [copySource, setCopySource] = useState("");
  const drop = useFileDrop((files) => void mgr.uploadFiles(files, color), !disabled);
  const uploads = mgr.uploads.filter((u) => u.color === color);

  if (ids.length === 0) {
    return (
      <div className="card" {...drop.dropProps} style={dropHighlight(drop.isOver)}>
        <div className="spread">
          <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>Fotos de {color}</div>
        </div>
        <div
          className="stack"
          style={{
            marginTop: ".75rem",
            alignItems: "center",
            textAlign: "center",
            border: ".0625rem dashed var(--color-line)",
            borderRadius: "var(--radius-md)",
            padding: "1.25rem",
            background: "var(--color-soft)",
          }}
        >
          <div style={{ fontWeight: 900, fontSize: "var(--text-lg)" }}>Sem foto própria</div>
          <p className="field__hint" style={{ maxWidth: "28rem" }}>
            Na loja esta cor mostra a galeria do produto. Para dar uma foto a ela,{" "}
            {disabled
              ? "solte fotos na subpasta desta cor no Drive,"
              : "envie fotos por aqui (botão abaixo ou arraste os arquivos para este cartão; vão para a subpasta desta cor no Drive),"}{" "}
            marque a cor em uma foto da Base (menu ⋯) ou copie de outra cor parecida. A foto
            continua na galeria do produto. Aparece em até {IMPORT_ETA_TEXT}.
          </p>
          <div className="cluster" style={{ justifyContent: "center" }}>
            <UploadButton color={color} disabled={disabled} onFiles={(files) => void mgr.uploadFiles(files, color)} />
            <a href={mgr.driveUrl} target="_blank" rel="noopener noreferrer" className="btn btn--secondary">
              <ExternalLink className="icon" aria-hidden />
              Abrir pasta no Drive
            </a>
          </div>
          <div className="cluster" style={{ justifyContent: "center" }}>
            <Select
              className="h-9 w-auto"
              value={copySource}
              disabled={disabled}
              onChange={(e) => setCopySource(e.target.value)}
              aria-label="Copiar fotos de qual cor?"
            >
              <option value="">Copiar fotos de…</option>
              {mgr.activeColors
                .filter((c) => c !== color && mgr.colorIds(c).length > 0)
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </Select>
            <Button
              type="button"
              variant="secondary"
              disabled={disabled || !copySource}
              onClick={() => {
                onCopyFromColor(copySource);
                setCopySource("");
              }}
            >
              Copiar
            </Button>
          </div>
        </div>
        <UploadList items={uploads} onClearFinished={mgr.clearFinishedUploads} />
      </div>
    );
  }

  return (
    <div className="card" {...drop.dropProps} style={dropHighlight(drop.isOver)}>
      <div className="spread">
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>Fotos de {color}</div>
        <span className="card__meta">{ids.length} foto{ids.length !== 1 ? "s" : ""} · a 1ª é a capa desta cor</span>
        <a href={mgr.driveUrl} target="_blank" rel="noopener noreferrer" className="btn btn--secondary">
          <ExternalLink className="icon" aria-hidden />
          Abrir subpasta no Drive
        </a>
      </div>
      <div style={{ marginTop: ".75rem" }}>
        <DndContext
          sensors={mgr.sensors}
          collisionDetection={closestCorners}
          onDragStart={mgr.onDragStart}
          onDragEnd={mgr.onDragEnd}
        >
          <PhotoGroup
            description=""
            ids={ids}
            orderMap={mgr.orderMap}
            allowedChannels={mgr.allowedChannels}
            activeColors={mgr.activeColors}
            slug={slug}
            disabled={disabled || mgr.orderMutation.isPending}
            onSetCover={(id) => mgr.coverMutation.mutate(id)}
            onToggleChannel={(id, channels) => mgr.channelsMutation.mutate({ media_id: id, channels })}
            onSetColors={(id, colors) => mgr.colorsMutation.mutate({ media_id: id, colors })}
            uploadColor={color}
            uploadDisabled={disabled}
            onUploadFiles={(files) => void mgr.uploadFiles(files, color)}
          />
        </DndContext>
      </div>
      <UploadList items={uploads} onClearFinished={mgr.clearFinishedUploads} />
    </div>
  );
}

function PendingFolderBlock({
  folder,
  count,
  activeColors,
  disabled,
  applying,
  onApply,
}: {
  folder: string;
  count: number;
  activeColors: string[];
  disabled: boolean;
  applying: boolean;
  onApply: (color: string) => void;
}): JSX.Element {
  const [chosen, setChosen] = useState("");
  return (
    <div className="cluster" style={{ border: ".0625rem solid var(--color-warn)", background: "var(--color-warn-soft)", borderRadius: "var(--radius-md)", padding: ".625rem .75rem" }}>
      <AlertTriangle className="icon" style={{ color: "var(--color-warn)" }} aria-hidden />
      <div className="grow" style={{ minWidth: "16em" }}>
        <b>Pasta "{folder}" não bate com nenhuma cor deste produto no Sisplan.</b>{" "}
        {count} foto{count > 1 ? "s estão" : " está"} sem cor até você escolher.
      </div>
      <label className="cluster" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
        É a cor
        <Select
          className="h-8 w-auto"
          value={chosen}
          disabled={disabled || applying}
          onChange={(e) => setChosen(e.target.value)}
          aria-label={`Cor correta para as fotos da pasta ${folder}`}
        >
          <option value="">escolher…</option>
          {activeColors.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </label>
      <Button type="button" size="sm" disabled={disabled || applying || !chosen} onClick={() => onApply(chosen)}>
        {applying ? "Aplicando…" : `Aplicar às ${count} foto${count > 1 ? "s" : ""}`}
      </Button>
    </div>
  );
}

function PhotoGroup({
  description,
  hint,
  ids,
  orderMap,
  allowedChannels,
  activeColors,
  slug,
  disabled,
  onSetCover,
  onToggleChannel,
  onSetColors,
  uploadColor,
  uploadDisabled,
  onUploadFiles,
}: {
  description: string;
  /** Texto de ajuda do grupo; padrao e o da cor (a 1a foto e a capa da cor). */
  hint?: string;
  ids: number[];
  orderMap: Map<number, MediaItem>;
  allowedChannels: string[];
  activeColors: string[];
  slug: string;
  disabled: boolean;
  onSetCover: (id: number) => void;
  onToggleChannel: (id: number, channels: string[]) => void;
  onSetColors: (id: number, colors: string[]) => void;
  /** Cor que o cartao "+ Enviar fotos" usa (null = Base). */
  uploadColor: string | null;
  uploadDisabled: boolean;
  onUploadFiles: (files: File[]) => void;
}): JSX.Element {
  return (
    <div>
      {description ? <span className="card__meta">{description}</span> : null}
      <p className="field__hint" style={{ marginTop: description ? ".125rem" : 0 }}>
        {hint ?? "Arraste para reordenar. A primeira foto é a capa desta cor na loja."}
      </p>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="cards cards--photos" style={{ marginTop: ".5rem" }}>
          {ids.map((id, idx) => {
            const m = orderMap.get(id);
            if (!m) return null;
            return (
              <PhotoCard
                key={id}
                media={m}
                order={idx + 1}
                allowedChannels={allowedChannels}
                activeColors={activeColors}
                currentSlug={slug}
                sortable
                onSetCover={() => onSetCover(id)}
                onToggleChannel={(channels) => onToggleChannel(id, channels)}
                onSetColors={(colors) => onSetColors(id, colors)}
                disabled={disabled}
              />
            );
          })}
          <UploadTile color={uploadColor} disabled={uploadDisabled} onFiles={onUploadFiles} />
        </div>
      </SortableContext>
    </div>
  );
}

/** Campo de arquivo escondido so visualmente (continua focavel por teclado:
 *  Tab chega, Enter/Espaco abre o seletor). Vive dentro de um <label>. */
function HiddenFileInput({
  label,
  onFiles,
}: {
  label: string;
  onFiles: (files: File[]) => void;
}): JSX.Element {
  return (
    <input
      type="file"
      multiple
      accept={UPLOAD_ACCEPT}
      className="sr-only"
      aria-label={label}
      onChange={(e) => {
        const files = Array.from(e.target.files ?? []);
        // Zera o campo: escolher o mesmo arquivo duas vezes tem que disparar de novo.
        e.target.value = "";
        if (files.length > 0) onFiles(files);
      }}
    />
  );
}

function uploadLabel(color: string | null): string {
  return color ? `Enviar fotos de ${color}` : "Enviar fotos";
}

/** Cartao "+ Enviar fotos" ao final do grid: clique abre o seletor, e o cartao
 *  inteiro da area tambem aceita arquivo solto (useFileDrop no pai). Some sem photos.write. */
function UploadTile({
  color,
  disabled,
  onFiles,
}: {
  color: string | null;
  disabled: boolean;
  onFiles: (files: File[]) => void;
}): JSX.Element | null {
  if (disabled) return null;
  return (
    <label
      // `relative`: o input sr-only e absoluto; sem referencia ele ancorava na
      // pagina e criava 1000 px de rolagem fantasma (QA P1-01, 27/10).
      className="card relative focus-within:ring-2 focus-within:ring-primary"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: ".25rem",
        aspectRatio: "1/1",
        border: ".0938rem dashed var(--color-line)",
        color: "var(--color-ink-3)",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <HiddenFileInput label={uploadLabel(color)} onFiles={onFiles} />
      <span style={{ fontSize: "var(--text-2xl)", fontWeight: 300, lineHeight: 1 }} aria-hidden>+</span>
      <span style={{ fontWeight: 800, fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>Enviar fotos</span>
      <span className="field__hint" style={{ padding: "0 .5rem" }}>clique ou arraste</span>
    </label>
  );
}

/** Botao primario "Enviar fotos" dos estados vazios (mesmo campo do UploadTile). */
function UploadButton({
  color,
  disabled,
  onFiles,
}: {
  color: string | null;
  disabled: boolean;
  onFiles: (files: File[]) => void;
}): JSX.Element | null {
  if (disabled) return null;
  return (
    <label
      className="btn btn--primary relative focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
      style={{ cursor: "pointer" }}
    >
      <HiddenFileInput label={uploadLabel(color)} onFiles={onFiles} />
      <span aria-hidden>+</span> Enviar fotos
    </label>
  );
}

const UPLOAD_STATUS_TEXT: Record<UploadStatus, string> = {
  fila: "Na fila",
  enviando: "Enviando…",
  enviada: `Enviada · importando (até ${IMPORT_ETA_TEXT})`,
  importada: "Já está na galeria",
  duplicada: "Já estava no Drive. Não enviei de novo.",
  erro: "Não enviada",
};

function UploadStatusIcon({ status }: { status: UploadStatus }): JSX.Element {
  switch (status) {
    case "enviando":
      return <Loader2 className="icon animate-spin" aria-hidden />;
    case "enviada":
      return <Clock className="icon" style={{ color: "var(--color-good)" }} aria-hidden />;
    case "importada":
      return <CheckCircle2 className="icon" style={{ color: "var(--color-good)" }} aria-hidden />;
    case "duplicada":
      return <Info className="icon" style={{ color: "var(--color-warn)" }} aria-hidden />;
    case "erro":
      return <AlertTriangle className="icon" style={{ color: "var(--color-bad)" }} aria-hidden />;
    default:
      return <Clock className="icon" style={{ color: "var(--color-ink-3)" }} aria-hidden />;
  }
}

/** Lista de envios abaixo do grid: miniatura local, nome e estado de cada arquivo. */
function UploadList({
  items,
  onClearFinished,
}: {
  items: UploadItem[];
  onClearFinished: () => void;
}): JSX.Element | null {
  if (items.length === 0) return null;
  const hasFinished = items.some((u) => u.status === "importada" || u.status === "duplicada" || u.status === "erro");
  return (
    <div style={{ marginTop: ".75rem" }}>
      <div className="spread">
        <div style={{ fontWeight: 900, fontSize: "var(--text-md)" }}>Envios</div>
        {hasFinished ? (
          <Button type="button" variant="ghost" size="sm" onClick={onClearFinished}>
            Limpar concluídos
          </Button>
        ) : null}
      </div>
      <ul
        className="stack"
        style={{ gap: ".375rem", marginTop: ".375rem", listStyle: "none", padding: 0 }}
      >
        {items.map((u) => (
          <li
            key={u.id}
            style={{
              display: "grid",
              gridTemplateColumns: "3rem minmax(0, 1fr)",
              alignItems: "center",
              gap: ".625rem",
              background: "var(--color-soft)",
              borderRadius: "var(--radius-md)",
              padding: ".375rem .5rem",
            }}
          >
            <img
              src={u.previewUrl}
              alt=""
              draggable={false}
              style={{ width: "3rem", height: "3rem", objectFit: "cover", borderRadius: "var(--radius-sm)" }}
            />
            <div style={{ minWidth: 0 }}>
              <div className="truncate" style={{ fontWeight: 700, fontSize: "var(--text-sm)" }}>
                {u.name}
                {u.color ? <span style={{ color: "var(--color-ink-2)", fontWeight: 600 }}> · {u.color}</span> : null}
              </div>
              <div
                className="cluster"
                style={{
                  gap: ".375rem",
                  fontSize: "var(--text-sm)",
                  color: u.status === "erro" ? "var(--color-bad)" : "var(--color-ink-2)",
                }}
              >
                <UploadStatusIcon status={u.status} />
                <span>
                  {u.status === "enviando"
                    ? u.progress >= 100
                      ? "Gravando no Drive…"
                      : `Enviando ${u.progress}%`
                    : u.status === "erro" && u.message
                      ? u.message
                      : UPLOAD_STATUS_TEXT[u.status]}
                </span>
              </div>
              {u.status === "enviando" ? (
                <div
                  role="progressbar"
                  aria-label={`Envio de ${u.name}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={u.progress}
                  style={{
                    height: ".25rem",
                    marginTop: ".25rem",
                    borderRadius: "var(--radius-pill)",
                    background: "var(--color-line)",
                    overflow: "hidden",
                  }}
                >
                  <div style={{ width: `${u.progress}%`, height: "100%", background: "var(--color-coral)" }} />
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface PhotoCardProps {
  media: MediaItem;
  /** Posição na ordem de exibição (1-based) — badge no canto sup. esquerdo,
   * substitui o nome de arquivo cru como identificador visual da foto. */
  order?: number;
  allowedChannels: string[];
  activeColors: string[];
  currentSlug: string;
  onSetCover: () => void;
  onToggleChannel: (channels: string[]) => void;
  onSetColors: (colors: string[]) => void;
  disabled?: boolean;
  notInChannel?: boolean;
  sortable?: boolean;
}

function PhotoCard({
  media,
  order,
  allowedChannels,
  activeColors,
  currentSlug,
  onSetCover,
  onToggleChannel,
  onSetColors,
  disabled,
  notInChannel,
  sortable,
}: PhotoCardProps): JSX.Element {
  const canDrag = sortable && !disabled && !notInChannel;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: Number(media.id),
    disabled: !canDrag,
  });

  const style = sortable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        touchAction: "none" as const,
      }
    : undefined;

  const inChannel = media.channels.includes(currentSlug);
  const colors = mediaColors(media);
  const isAllColors = colors.length === 0;
  const divergesFrom = manualDivergesFromFolder(media);
  const [moveOpen, setMoveOpen] = useState(false);

  const toggleChannel = (c: string): void => {
    const next = media.channels.includes(c)
      ? media.channels.filter((x) => x !== c)
      : Array.from(new Set([...media.channels, c]));
    onToggleChannel(next);
  };

  return (
    <div
      ref={sortable ? setNodeRef : undefined}
      style={style}
      className={cn(
        "card group relative",
        canDrag ? "cursor-grab active:cursor-grabbing" : "",
        media.is_cover ? "ring-2 ring-primary" : "",
        !inChannel ? "opacity-60" : "",
      )}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
    >
      <div className="thumb relative" style={{ width: "100%", aspectRatio: "1/1" }}>
        {media.url ? (
          <img src={media.url} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover select-none" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-ink-3">
            <ImageOff className="h-8 w-8" aria-hidden />
          </div>
        )}

        {/* Numero da ordem — identificador visual no lugar do nome de arquivo
            cru (reprovacao Wladimir 23/09: cartao nao mostra tecnicalidade). */}
        {order != null || !isAllColors ? (
          <div
            className="absolute pointer-events-none"
            style={{
              top: ".5rem",
              left: ".5rem",
              right: "2.5rem",
              display: "flex",
              alignItems: "center",
              gap: ".25rem",
              minWidth: 0,
            }}
          >
            {order != null ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "1.5rem",
                  height: "1.5rem",
                  padding: "0 .375rem",
                  borderRadius: "var(--radius-pill)",
                  background: "rgba(0,0,0,.55)",
                  color: "#fff",
                  fontSize: "var(--text-sm)",
                  fontWeight: 900,
                  flexShrink: 0,
                }}
                aria-hidden
              >
                {order}
              </span>
            ) : null}
            {/* Etiqueta da cor: a foto continua na galeria do produto e tambem
                representa esta(s) cor(es). Sem ela a operadora nao sabe, na
                Base, qual foto ja tem cor (pedido Erica/Hiit 2026-10-13). */}
            {!isAllColors ? (
              <span
                className="truncate"
                role="img"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: ".25rem",
                  height: "1.5rem",
                  padding: "0 .5rem",
                  borderRadius: "var(--radius-pill)",
                  // Mesma pilula escura do numero: a foto e clara no geral e o
                  // tema pode ser escuro (var(--color-ink) clara sobre pilula
                  // clara sumia no smoke de 2026-10-13).
                  background: "rgba(0,0,0,.55)",
                  color: "#fff",
                  fontSize: "var(--text-sm)",
                  fontWeight: 900,
                  minWidth: 0,
                }}
                title={`Também é a foto de: ${colors.join(", ")}`}
                aria-label={`Também é a foto de: ${colors.join(", ")}`}
              >
                <ColorSwatch name={colors[0] ?? ""} size=".75rem" />
                {/* No celular o cartao e estreito e o nome virava "B.." (QA P2-01):
                    abaixo de sm fica so a bolinha; o nome vai no aria-label. */}
                <span className="truncate hidden sm:inline">{colors.length === 1 ? colors[0] : `${colors[0] ?? ""} +${colors.length - 1}`}</span>
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Grip de arrastar — so aparece no hover/foco do cartao, junto do ⋯. */}
        {canDrag ? (
          <div
            className="absolute pointer-events-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
            style={{ bottom: ".5rem", left: ".5rem", display: "inline-flex", alignItems: "center", justifyContent: "center", height: "1.75rem", width: "1.75rem", borderRadius: "var(--radius-md)", background: "rgba(255,255,255,.85)" }}
            aria-hidden
          >
            <GripVertical className="icon" style={{ color: "var(--color-ink-2)" }} />
          </div>
        ) : null}

        {inChannel ? (
          <button
            type="button"
            onClick={onSetCover}
            disabled={disabled}
            className="absolute btn btn--icon"
            style={{ top: ".5rem", right: ".5rem", height: "1.75rem", width: "1.75rem", minHeight: 0, background: "rgba(255,255,255,.85)", color: media.is_cover ? "var(--color-coral)" : "var(--color-ink-2)" }}
            aria-label={media.is_cover ? "Capa atual deste canal" : "Definir como capa deste canal"}
            title={media.is_cover ? "Capa atual deste canal" : "Definir como capa deste canal"}
          >
            <Star className="icon" style={media.is_cover ? { fill: "currentColor" } : undefined} />
          </button>
        ) : null}

        {/* ⋯ — canais + mover para cor, tudo dentro do menu. So aparece no
            hover/foco do cartao (reprovacao Wladimir 23/09: cartao nao pode
            mostrar nome de arquivo nem linha fixa de chip/canal embaixo). */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMoveOpen(true)}
          className="absolute btn btn--icon opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
          style={{ bottom: ".5rem", right: ".5rem", height: "1.75rem", width: "1.75rem", minHeight: 0, background: "rgba(255,255,255,.85)", color: "var(--color-ink-2)" }}
          aria-label="Mais ações da foto"
          title="Canais, cor e mais ações"
        >
          <MoreHorizontal className="icon" aria-hidden />
        </button>
      </div>

      {divergesFrom ? (
        <div style={{ fontSize: "var(--text-sm)", color: "var(--color-warn)", marginTop: ".375rem" }}>
          marcada como {colors.join(", ")}, mas está na pasta {divergesFrom}
        </div>
      ) : null}

      <MovePhotoDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        activeColors={activeColors}
        currentColors={colors}
        isAllColors={isAllColors}
        allowedChannels={allowedChannels}
        channels={media.channels}
        onToggleChannel={toggleChannel}
        channelsDisabled={disabled}
        onUseAsCommon={() => {
          onSetColors([]);
          setMoveOpen(false);
        }}
        onApplyColors={(next) => {
          onSetColors(next);
          setMoveOpen(false);
        }}
      />
    </div>
  );
}

/* ============================================================
 * "Cores da foto…" — seletor com busca e MULTIPLA escolha (substitui os
 * chips soltos). Uma foto pode servir a 2+ cores (ex.: foto com duas
 * modelos), como era com os chips; review fidelidade-3 pegou a perda.
 * ============================================================ */
function MovePhotoDialog({
  open,
  onOpenChange,
  activeColors,
  currentColors,
  isAllColors,
  allowedChannels,
  channels,
  onToggleChannel,
  channelsDisabled,
  onUseAsCommon,
  onApplyColors,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeColors: string[];
  currentColors: string[];
  isAllColors: boolean;
  allowedChannels: string[];
  channels: string[];
  onToggleChannel: (channel: string) => void;
  channelsDisabled?: boolean;
  onUseAsCommon: () => void;
  onApplyColors: (colors: string[]) => void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<string[]>(currentColors);
  const toggleDraft = (c: string) =>
    setDraft((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  const changed =
    draft.length !== currentColors.length || draft.some((c) => !currentColors.includes(c));
  const filtered = activeColors.filter((c) => c.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) setDraft(currentColors);
        if (!next) setQuery("");
      }}
    >
      <DialogContent style={{ width: "min(24rem, 92vw)" }}>
        <DialogHeader>
          <DialogTitle>Ações da foto</DialogTitle>
          <DialogDescription>
            {isAllColors
              ? "Sem cor específica: só na galeria do produto"
              : `Na galeria do produto e também é a foto de: ${currentColors.join(", ")}`}
          </DialogDescription>
        </DialogHeader>

        {allowedChannels.length > 0 ? (
          <div style={{ marginTop: ".75rem" }}>
            <span className="field__hint">Canais</span>
            <div className="cluster" style={{ marginTop: ".25rem", gap: ".375rem" }}>
              {allowedChannels.map((c) => {
                const checked = channels.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    disabled={channelsDisabled}
                    onClick={() => onToggleChannel(c)}
                    aria-pressed={checked}
                    className="chip"
                    style={
                      checked
                        ? { background: "var(--color-coral-soft)", color: "var(--color-coral)", border: ".0625rem solid var(--color-coral)" }
                        : { cursor: "pointer" }
                    }
                    title={`Canal ${c}${checked ? " — ligado, clique para desligar" : " — desligado, clique para ligar"}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <Button
          type="button"
          variant={currentColors.length === 0 ? "primary" : "secondary"}
          onClick={onUseAsCommon}
          style={{ width: "100%", marginTop: ".75rem" }}
        >
          Tirar a cor (fica só na galeria do produto)
        </Button>

        <div
          className="cluster"
          style={{
            marginTop: ".75rem",
            background: "var(--color-soft)",
            border: ".0625rem solid var(--color-line)",
            borderRadius: "var(--radius-pill)",
            minHeight: "var(--size-tap-desktop)",
            padding: "0 .75rem",
          }}
        >
          <Search className="icon" style={{ color: "var(--color-ink-3)" }} aria-hidden />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar cor…"
            aria-label="Buscar cor"
            className="border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
            style={{ flex: 1, minWidth: 0, border: 0, background: "transparent", outline: "none" }}
          />
        </div>

        <div
          className="scroll-silent stack"
          style={{ marginTop: ".5rem", maxHeight: "18rem", overflowY: "auto", gap: ".125rem" }}
        >
          {filtered.length === 0 ? (
            <p className="field__hint" style={{ padding: ".5rem" }}>Nenhuma cor bate com a busca.</p>
          ) : (
            filtered.map((c) => {
              const active = draft.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  role="checkbox"
                  aria-checked={active}
                  onClick={() => toggleDraft(c)}
                  className="cluster"
                  style={{
                    width: "100%",
                    minHeight: "2.5rem",
                    padding: "0 .625rem",
                    borderRadius: "var(--radius-md)",
                    border: 0,
                    cursor: "pointer",
                    background: active ? "var(--color-coral-soft)" : "transparent",
                    color: active ? "var(--color-coral)" : "var(--color-ink)",
                    fontWeight: active ? 900 : 700,
                    fontSize: "var(--text-base)",
                    textAlign: "left",
                  }}
                >
                  <ColorSwatch name={c} size=".875rem" />
                  <span className="grow truncate">{c}</span>
                  {active ? <span aria-hidden>✓</span> : null}
                </button>
              );
            })
          )}
        </div>

        <div className="spread" style={{ marginTop: ".75rem" }}>
          <span className="field__hint">
            {draft.length === 0 ? "Nenhuma cor marcada: fica só na galeria do produto" : `${draft.length} cor${draft.length === 1 ? "" : "es"} marcada${draft.length === 1 ? "" : "s"} · continua na galeria do produto`}
          </span>
          <Button type="button" onClick={() => onApplyColors(draft)} disabled={!changed}>
            Aplicar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
